import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithErrorHandling } from "../baseQuery";

export const careersApi = createApi({
  reducerPath: "careersApi",
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ["Jobs", "Applications", "Categories"],
  endpoints: (builder) => ({
    // Categories
    getJobCategories: builder.query({
      query: () => "/job-categories",
      providesTags: ["Categories"],
    }),
    createJobCategory: builder.mutation({
      query: (data) => ({
        url: "/job-categories",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Categories"],
    }),
    updateJobCategory: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/job-categories/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["Categories"],
    }),
    deleteJobCategory: builder.mutation({
      query: (id) => ({
        url: `/job-categories/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Categories"],
    }),
    toggleJobCategoryStatus: builder.mutation({
      query: (id) => ({
        url: `/job-categories/${id}/status`,
        method: "PATCH",
      }),
      invalidatesTags: ["Categories"],
    }),

    // Jobs
    getJobs: builder.query({
      query: (params) => ({
        url: "/jobs",
        params,
      }),
      providesTags: ["Jobs"],
    }),
    getJobBySlug: builder.query({
      query: (slug) => `/jobs/slug/${slug}`,
      providesTags: (result, error, slug) => [{ type: "Jobs", id: slug }],
    }),
    getJobById: builder.query({
      query: (id) => `/jobs/${id}`,
      providesTags: (result, error, id) => [{ type: "Jobs", id }],
    }),
    createJob: builder.mutation({
      query: (data) => ({
        url: "/jobs",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Jobs"],
    }),
    updateJob: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/jobs/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        "Jobs",
        { type: "Jobs", id },
      ],
    }),
    deleteJob: builder.mutation({
      query: (id) => ({
        url: `/jobs/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Jobs"],
    }),
    toggleJobStatus: builder.mutation({
      query: (id) => ({
        url: `/jobs/${id}/status`,
        method: "PATCH",
      }),
      invalidatesTags: ["Jobs"],
    }),

    // Applications
    getApplications: builder.query({
      query: (params) => ({
        url: "/applications",
        params,
      }),
      providesTags: ["Applications"],
    }),
    getApplicationById: builder.query({
      query: (id) => `/applications/${id}`,
      providesTags: (result, error, id) => [{ type: "Applications", id }],
    }),
    submitApplication: builder.mutation({
      query: (data) => ({
        url: "/applications",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Applications"],
    }),
    updateApplicationStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/applications/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        "Applications",
        { type: "Applications", id },
      ],
    }),
    deleteApplication: builder.mutation({
      query: (id) => ({
        url: `/applications/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Applications"],
    }),
  }),
});

export const {
  useGetJobsQuery,
  useGetJobBySlugQuery,
  useGetJobByIdQuery,
  useCreateJobMutation,
  useUpdateJobMutation,
  useDeleteJobMutation,
  useToggleJobStatusMutation,
  useGetApplicationsQuery,
  useGetApplicationByIdQuery,
  useSubmitApplicationMutation,
  useUpdateApplicationStatusMutation,
  useDeleteApplicationMutation,
  useGetJobCategoriesQuery,
  useCreateJobCategoryMutation,
  useUpdateJobCategoryMutation,
  useDeleteJobCategoryMutation,
  useToggleJobCategoryStatusMutation,
} = careersApi;
